import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Package, Users, TrendingUp, Box, FolderTree, Briefcase, Tag, ShoppingCart, ArrowLeft, ClipboardList, Receipt, Link2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + "/api/procurement";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, isSuperAdmin, isCompanyAdmin } = useAuth();

  // Check sub-permission
  const hasSubPermission = (subPerm) => {
    if (isSuperAdmin || isCompanyAdmin) return true;
    const permissions = user?.permissions || [];
    if (permissions.includes('all')) return true;
    // إذا كان لديه صلاحية المشتريات الكاملة ولا توجد صلاحيات فرعية محددة، يرى الكل
    const procurementSubKeys = [
      'proc_categories', 'proc_items', 'proc_suppliers', 'proc_projects',
      'proc_quotes', 'proc_comparison', 'proc_tags', 'proc_invoices', 'proc_receipts',
      'proc_invoice_matching'
    ];
    const hasAnySub = procurementSubKeys.some(k => permissions.includes(k));
    if (permissions.includes('procurement') && !hasAnySub) {
      return true; // legacy: procurement only = full access
    }
    return permissions.includes(subPerm);
  };

  const allMenuItems = [
    {
      title: 'التصنيفات الرئيسية',
      description: 'إدارة التصنيفات',
      icon: FolderTree,
      path: '/procurement/categories',
      color: 'from-blue-500 to-blue-600',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      testId: 'categories-menu-btn',
      permission: 'proc_categories'
    },
    {
      title: 'إدارة الأصناف',
      description: 'إضافة وإدارة الأصناف',
      icon: Box,
      path: '/procurement/items',
      color: 'from-indigo-500 to-indigo-600',
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      testId: 'items-menu-btn',
      permission: 'proc_items'
    },
    {
      title: 'إدارة الموردين',
      description: 'بيانات الموردين',
      icon: Users,
      path: '/procurement/suppliers',
      color: 'from-emerald-500 to-emerald-600',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      testId: 'suppliers-menu-btn',
      permission: 'proc_suppliers'
    },
    {
      title: 'إدارة المشاريع',
      description: 'إضافة وإدارة المشاريع',
      icon: Briefcase,
      path: '/procurement/projects',
      color: 'from-teal-500 to-teal-600',
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      testId: 'projects-menu-btn',
      permission: 'proc_projects'
    },
    {
      title: 'عروض الأسعار',
      description: 'متابعة العروض',
      icon: FileText,
      path: '/procurement/quotes',
      color: 'from-violet-500 to-violet-600',
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
      testId: 'quotes-menu-btn',
      permission: 'proc_quotes'
    },
    {
      title: 'تقرير المقارنة',
      description: 'مقارنة وتصدير',
      icon: TrendingUp,
      path: '/procurement/comparison',
      color: 'from-amber-500 to-amber-600',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      testId: 'comparison-menu-btn',
      permission: 'proc_comparison'
    },
    {
      title: 'التصنيفات',
      description: 'تصنيفات العروض',
      icon: Tag,
      path: '/procurement/tags',
      color: 'from-pink-500 to-pink-600',
      iconBg: 'bg-pink-100',
      iconColor: 'text-pink-600',
      testId: 'tags-menu-btn',
      permission: 'proc_tags'
    },
    {
      title: 'تسليم الفواتير',
      description: 'تسليم فواتير الموردين للحسابات',
      icon: ClipboardList,
      path: '/procurement/invoice-delivery',
      color: 'from-cyan-500 to-cyan-600',
      iconBg: 'bg-cyan-100',
      iconColor: 'text-cyan-600',
      testId: 'invoice-delivery-menu-btn',
      permission: 'proc_invoices'
    },
    {
      title: 'إدارة الاستلامات',
      description: 'تسجيل واعتماد استلامات الموردين',
      icon: Receipt,
      path: '/procurement/receipts',
      color: 'from-orange-500 to-orange-600',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      testId: 'receipts-menu-btn',
      permission: 'proc_receipts'
    },
    {
      title: 'مطابقة فواتير الموردين',
      description: 'ربط فواتير الموردين بالاستلامات المعتمدة',
      icon: Link2,
      path: '/procurement/invoice-matching',
      color: 'from-purple-500 to-fuchsia-600',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      testId: 'invoice-matching-menu-btn',
      permission: 'proc_invoice_matching'
    },
  ];

  const menuItems = allMenuItems.filter(item => hasSubPermission(item.permission));

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="text-center mb-5 sm:mb-8">
        <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl mb-3 shadow-lg">
          <ShoppingCart className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
        </div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 mb-1">
          شركة الغزالي
        </h1>
        <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-blue-600 mb-2">
          نظام مقارنة عروض الأسعار
        </h2>
        <p className="text-xs sm:text-sm text-gray-500 max-w-md mx-auto px-4">
          نظام متكامل لإدارة عروض الأسعار من الموردين وإنشاء تقارير المقارنة
        </p>
      </div>

      {/* Empty state */}
      {menuItems.length === 0 && (
        <div data-testid="no-procurement-access" className="text-center py-12 bg-white rounded-xl border border-gray-100">
          <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">لا توجد صلاحيات للوصول لأي قسم في نظام المشتريات</p>
        </div>
      )}

      {/* Menu Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <Card 
              key={item.path}
              data-testid={item.testId}
              className="group cursor-pointer hover:shadow-lg transition-all duration-300 border-0 overflow-hidden bg-white active:scale-[0.98]"
              onClick={() => navigate(item.path)}
            >
              <CardHeader className="p-3 sm:p-4 pb-2 sm:pb-3">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300 mb-2 sm:mb-3`}>
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <CardTitle className="text-sm sm:text-base text-gray-800 leading-tight">
                  {item.title}
                </CardTitle>
                <CardDescription className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                  {item.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0">
                <Button 
                  className={`w-full bg-gradient-to-r ${item.color} hover:shadow-md transition-all duration-300 text-xs sm:text-sm h-8 sm:h-10`}
                >
                  <span>الدخول</span>
                  <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
